import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import api from '../services/api';

interface ZoomAccount {
  id: string;
  zoom_email: string;
  account_type: string;
  created_at: string;
}

interface DashboardStats {
  zoom_accounts: number;
  meetings: number;
  embeds: number;
}

export default function DashboardPage() {
  const { user, logout } = useAuthStore();
  const [stats, setStats] = useState<DashboardStats>({ zoom_accounts: 0, meetings: 0, embeds: 0 });
  const [zoomAccounts, setZoomAccounts] = useState<ZoomAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectingZoom, setConnectingZoom] = useState(false);

  useEffect(() => {
    loadDashboardData();

    // Check for Zoom connection callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('zoom_connected') === 'true') {
      // Clear the URL param and refresh data
      window.history.replaceState({}, '', '/dashboard');
      loadDashboardData();
    }
    if (urlParams.get('zoom_error')) {
      alert(`Zoom connection error: ${urlParams.get('zoom_error')}`);
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [meResponse, accountsResponse] = await Promise.all([
        api.getMe(),
        api.getZoomAccounts(),
      ]);
      setStats(meResponse.stats);
      setZoomAccounts(accountsResponse.accounts || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnectZoom = async () => {
    try {
      setConnectingZoom(true);
      const response = await api.getZoomAuthUrl();
      window.location.href = response.authorization_url;
    } catch (error) {
      console.error('Failed to get Zoom auth URL:', error);
      alert('Failed to connect to Zoom. Please try again.');
      setConnectingZoom(false);
    }
  };

  const handleDisconnectZoom = async (accountId: string) => {
    if (!confirm('Are you sure you want to disconnect this Zoom account?')) {
      return;
    }
    try {
      await api.disconnectZoomAccount(accountId);
      loadDashboardData();
    } catch (error) {
      console.error('Failed to disconnect Zoom account:', error);
      alert('Failed to disconnect Zoom account.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-indigo-600">EmbedZoom</h1>
          <div className="flex items-center space-x-4">
            <span className="text-gray-700">{user?.email}</span>
            <button
              onClick={logout}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Logout
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8">
          Dashboard
        </h2>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Zoom Accounts</h3>
            <p className="text-3xl font-bold text-purple-600">
              {loading ? '...' : stats.zoom_accounts}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Total Meetings</h3>
            <p className="text-3xl font-bold text-indigo-600">
              {loading ? '...' : stats.meetings}
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-2">Active Embeds</h3>
            <p className="text-3xl font-bold text-green-600">
              {loading ? '...' : stats.embeds}
            </p>
          </div>
        </div>

        {/* Zoom Connection Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">Zoom Accounts</h3>
            <button
              onClick={handleConnectZoom}
              disabled={connectingZoom}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4.5 4.5h9a1.5 1.5 0 011.5 1.5v9a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 013 15V6a1.5 1.5 0 011.5-1.5zm12.75 3.75L21 5.5v10l-3.75-2.75v-4.5z"/>
              </svg>
              <span>{connectingZoom ? 'Connecting...' : 'Connect Zoom'}</span>
            </button>
          </div>

          {zoomAccounts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <p className="mb-2">No Zoom accounts connected</p>
              <p className="text-sm">Connect your Zoom account to start creating embeddable meetings</p>
            </div>
          ) : (
            <div className="space-y-3">
              {zoomAccounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <svg className="w-6 h-6 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M4.5 4.5h9a1.5 1.5 0 011.5 1.5v9a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 013 15V6a1.5 1.5 0 011.5-1.5zm12.75 3.75L21 5.5v10l-3.75-2.75v-4.5z"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-medium">{account.zoom_email}</p>
                      <p className="text-sm text-gray-500 capitalize">{account.account_type} Account</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDisconnectZoom(account.id)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded"
                  >
                    Disconnect
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Quick Actions</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link
              to="/meetings/create"
              className={`p-6 border-2 border-dashed rounded-lg text-center transition-colors ${
                zoomAccounts.length === 0
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
                  : 'border-gray-300 hover:border-indigo-500 hover:bg-indigo-50'
              }`}
              onClick={(e) => {
                if (zoomAccounts.length === 0) {
                  e.preventDefault();
                  alert('Please connect a Zoom account first');
                }
              }}
            >
              <div className="text-4xl mb-2">➕</div>
              <h4 className="font-semibold">Create New Meeting</h4>
              <p className="text-gray-600 text-sm mt-1">
                Set up a new Zoom meeting to embed
              </p>
            </Link>

            <Link
              to="/meetings"
              className="p-6 border-2 border-dashed border-gray-300 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors text-center"
            >
              <div className="text-4xl mb-2">📋</div>
              <h4 className="font-semibold">View All Meetings</h4>
              <p className="text-gray-600 text-sm mt-1">
                Manage your existing meetings
              </p>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
